import { useMemo } from "react"
import type { MeetingInspirePanel as MeetingInspirePanelInfo } from "services/api/meetingsClient"
import type { InspireControlAction } from "../../inspireControlMessages"
import MeetingInspirePanel from "../../MeetingInspirePanel"
import WiseFriendControlsPanel from "../../WiseFriendControlsPanel"
import type { InspireEmbedContext } from "../types"
import { getPanelTokenFromUrl } from "../shared/panelUrls"

export type InspireStagePlacement = {
  /** A card or shared response is live for everyone right now. */
  active: boolean
  /** The live card is on this attendee's stage (the default when a card goes live). */
  onStage: boolean
  setOnStage: (onStage: boolean) => void
}

type InspireInlineProps = {
  meetingId: string
  panel: MeetingInspirePanelInfo
  onControlAction: (action: InspireControlAction, activityId: string) => Promise<boolean>
  refreshKey?: number
  /** Where the live card is shown; omitted when the surface has no stage placement (tests, previews). */
  placement?: InspireStagePlacement
}

function StagePlacementBar({ placement, listener }: { placement: InspireStagePlacement; listener: boolean }) {
  if (!placement.active) return null
  if (placement.onStage) {
    return (
      <div data-testid="call-inspire-on-stage" className="m-3 flex shrink-0 items-center justify-between gap-3 rounded-xl bg-white/5 px-3 py-2 text-xs text-white/80 ring-1 ring-white/10">
        <span>{listener ? "The card is on the stage." : "Everyone sees the card on the stage."}</span>

      </div>
    )
  }
  return (
    <div data-testid="call-inspire-off-stage" className="m-3 flex shrink-0 items-center justify-between gap-3 rounded-xl bg-white/5 px-3 py-2 text-xs text-white/80 ring-1 ring-white/10">
      <span>The card is hidden from your stage.</span>
      <button type="button" onClick={() => placement.setOnStage(true)} className="shrink-0 rounded-full bg-[#f6a35c] px-2.5 py-1 font-semibold text-[#1c2530] hover:bg-[#f7b071]">
        Show on stage
      </button>
    </div>
  )
}

/**
 * Inspire Cards as first-class WiseFriend UI inside the custom surface. Presenters get the
 * WiseFriend controls (deck/card launch, live responses, review, sharing); everyone else
 * gets the card and response view. No Daily custom integration or iframe is involved.
 */
export function InspireInline({ meetingId, panel, onControlAction, refreshKey, placement }: InspireInlineProps) {
  const panelToken = useMemo(() => getPanelTokenFromUrl(panel.url), [panel.url])
  const embed = useMemo<InspireEmbedContext>(() => ({ meetingId, panelToken, onControlAction, refreshKey }), [meetingId, onControlAction, panelToken, refreshKey])

  if (!panel.enabled || !panelToken) {
    return (
      <p data-testid="call-inspire-unavailable" className="p-4 text-sm text-white/60">
        Inspire Cards are not available for this meeting.
      </p>
    )
  }

  const listener = panel.role !== "presenter"
  // While a listener's card is on the stage, the tab only points there: a second card view here
  // would double the polling and split the response form between two places.
  const cardOnStageOnly = listener && Boolean(placement?.active && placement.onStage)
  return (
    <div data-testid="call-inspire-inline" data-inspire-role={panel.role} className="flex h-full w-full flex-col">
      {placement && <StagePlacementBar placement={placement} listener={listener} />}
      {!cardOnStageOnly && (
        <div className="relative min-h-0 flex-1 overflow-hidden">
          {listener ? <MeetingInspirePanel embed={embed} /> : <WiseFriendControlsPanel embed={embed} />}
        </div>
      )}
    </div>
  )
}
